import React, { memo, useEffect, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  TableSortLabel,
  Box,
} from "@mui/material";
import sort from "../../assets/images/svg/sort.svg";
import { useDashboardTable } from "../../hooks/useDashboardTable";

const DashboardTable = ({
  rows = [],
  columns = [],
  navigate,
  renderExpandedRow,
  emptyMessage = "No Data",
  tableSx = {},
  headCellSx = {},
  bodyCellSx = {},
  rowSx = {},
  containerSx = {},
  onRowClick,
  p = 2,
  loadMore,
}) => {
  const {
    expandedRowId,
    sortedRows,
    handleExpandClick,
    handleSort,
    order,
    orderBy,
    location,
    getNestedValue,
    refe,
  } = useDashboardTable(rows);

  return (
    <Box p={p}>
      <Paper elevation={2}>
        <TableContainer sx={{ maxHeight: "67vh", ...containerSx }}>
          <Table
            size="small"
            stickyHeader
            sx={{ ...tableSx, tableLayout: "fixed" }}
          >
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.id}
                    align={col.align || "left"}
                    sx={{
                      backgroundColor: "#683C8C",
                      color: "#fff",
                      cursor: "pointer",
                      padding: "16px 6px",
                      textWrap: "nowrap",
                      width: 150,
                      ...headCellSx,
                      ...(col.headSx || {}),
                    }}
                  >
                    {col.headerRender ? (
                      col.headerRender()
                    ) : (
                      <TableSortLabel
                        active={orderBy === col.id}
                        direction={orderBy === col.id ? order : "asc"}
                        onClick={() =>
                          col.id !== "action" && handleSort(col.id)
                        }
                        IconComponent={() =>
                          col.isSortable || col.id !== "action" ? (
                            <img
                              alt="sort-icon"
                              src={sort}
                              style={{
                                marginLeft: "10px",
                                width: "10px",
                                height: "10px",
                              }}
                            />
                          ) : null
                        }
                        sx={{
                          color: "#fff !important",
                          "&.Mui-active": { color: "#fff" },
                          "& .MuiTableSortLabel-icon": {
                            color: "#fff !important",
                            opacity: 1,
                          },
                        }}
                      >
                        {col.label}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedRows.map((row, i) => (
                <React.Fragment key={i}>
                  <TableRow
                    hover
                    sx={{
                      ...rowSx,
                      cursor: onRowClick ? "pointer" : "default",
                    }}
                    onClick={() => onRowClick?.(row)}
                  >
                    {columns.map((col) => (
                      <TableCell
                        key={col.id}
                        align={col.align || "left"}
                        sx={{
                          fontSize: "12px",
                          padding: "10px 6px",
                          color: "rgb(86, 93, 109)",
                          ...bodyCellSx,
                          ...(col.bodySx || {}),
                        }}
                      >
                        {col.renderCell
                          ? col.renderCell(
                              row,
                              navigate,
                              handleExpandClick,
                              expandedRowId
                            )
                          : getNestedValue(row, col.id)}
                      </TableCell>
                    ))}
                  </TableRow>

                  {renderExpandedRow && (
                    <TableRow
                      ref={row._id === location.state?.id ? refe : null}
                      sx={{ backgroundColor: "rgba(221, 202, 238, 0.35)" }}
                    >
                      <TableCell
                        colSpan={columns.length}
                        sx={{ paddingBottom: 0, paddingTop: 0 }}
                      >
                        <Collapse
                          in={expandedRowId === (row.id || row._id)}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box m={2}>{renderExpandedRow(row)}</Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
              {rows.length < loadMore?.total && (
                <TableRow sx={{ cursor: "pointer" }}>
                  <TableCell
                    onClick={loadMore.loadmore}
                    sx={{
                      fontSize: "12px",
                      padding: "10px 6px",
                      color: "black",
                      textDecoration: "underline",
                      textAlign: "center",
                    }}
                    colSpan={columns.length}
                  >
                    Load{" "}
                    {loadMore?.total - rows.length >= 20
                      ? "20"
                      : loadMore?.total - rows.length}{" "}
                    more
                  </TableCell>
                </TableRow>
              )}
              {rows.length < 1 && (
                <TableRow sx={{ height: "50vh" }}>
                  <TableCell
                    colSpan={columns.length}
                    sx={{
                      textAlign: "center",
                      color: "black",
                      fontSize: "16px",
                    }}
                  >
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default memo(DashboardTable);
